import type { TUserOrganization } from '@src/models/interfaces/user.interface';
import { resolveUserDisplayName } from '@src/packages/utils/user';
import UserRepository from '@src/repositories/UserRepository';

export interface EmergencyParticipantProfile {
  displayName: string;
  photoURL?: string;
}

export class EmergencyParticipantService {
  private readonly userRepository: UserRepository;
  private readonly profilesByOrganization = new Map<
    string,
    Map<string, EmergencyParticipantProfile>
  >();

  constructor(userRepository = new UserRepository()) {
    this.userRepository = userRepository;
  }

  async resolveNames(organizationId: string, userIds: string[]): Promise<Record<string, string>> {
    const profiles = await this.resolveProfiles(organizationId, userIds);
    return Object.fromEntries(
      Object.entries(profiles).map(([userId, profile]) => [userId, profile.displayName]),
    );
  }

  async resolveProfiles(
    organizationId: string,
    userIds: string[],
  ): Promise<Record<string, EmergencyParticipantProfile>> {
    const uniqueUserIds = userIds.filter(
      (userId, index, ids) => Boolean(userId) && ids.indexOf(userId) === index,
    );
    const cachedProfiles =
      this.profilesByOrganization.get(organizationId) ??
      new Map<string, EmergencyParticipantProfile>();
    this.profilesByOrganization.set(organizationId, cachedProfiles);
    const entries = await Promise.all(
      uniqueUserIds.map(async userId => {
        const cached = cachedProfiles.get(userId);
        if (cached) return [userId, cached] as const;
        let user: TUserOrganization | null = null;
        try {
          user = await this.userRepository.getUser(organizationId, userId);
        } catch {
          return [userId, { displayName: '' }] as const;
        }
        const profile: EmergencyParticipantProfile = user
          ? {
              displayName: resolveUserDisplayName(user, ''),
              photoURL: user.photoURL || undefined,
            }
          : { displayName: '' };
        if (user) cachedProfiles.set(userId, profile);
        return [userId, profile] as const;
      }),
    );

    return Object.fromEntries(entries);
  }
}
